// ═══════════════════════════════════════════════════════════════════════════
// @dxf-ai-editor/command-registry — attribute category descriptors
// ═══════════════════════════════════════════════════════════════════════════
//
// Block-attribute (ATTDEF / ATTRIB) command surface. Covers:
//
//   • ATTDEF   — author a new ATTDEF (block attribute definition) inside
//                a BEDIT session. Reducer literal:
//                `geometry/create-attdef`.
//   • DDATTE   — open the simple, table-style attribute editor for an
//                existing INSERT. Aliased to AutoCAD's ATTEDIT for
//                muscle-memory parity. Underlying literal:
//                `geometry/update-attribute-value` (single tag) plus
//                `geometry/update-attribute-values` (batch save).
//   • EATTEDIT — open the enhanced (tabbed) attribute editor for an
//                INSERT. Same literals as DDATTE plus the property-tab
//                payload `insertProperties` on
//                `geometry/update-attribute-values`.
//   • ATTSYNC  — synchronise ATTRIB rows on every INSERT of a block with
//                the current ATTDEF template. Reducer literal:
//                `geometry/attsync`. The BATTMAN authoring literals
//                (`geometry/remove-attdef`, `geometry/update-attdef`,
//                `geometry/rename-attdef-tag`, `geometry/reorder-attdefs`)
//                are not user-facing CLI commands — they are dispatched
//                from within the BATTMAN dialog. We attach them to ATTSYNC
//                here as related literals so coverage validation passes
//                without inventing a synthetic BATTMAN descriptor before
//                the dialog is exposed via its own slot.
//
// Reducer locations were located via `grep -n 'case "geometry/create-attdef"'`
// (and siblings) in `apps/web/src/app/state/geometryReducer.ts`. The
// underlying handlers live in `attributeReducerHelpers.ts`.
// ═══════════════════════════════════════════════════════════════════════════

import type { CommandDescriptor } from "../types.js";

const GEOMETRY_REDUCER = "apps/web/src/app/state/geometryReducer.ts";

export const ATTRIBUTE_DESCRIPTORS: readonly CommandDescriptor[] = [
  {
    id: "ATTDEF",
    category: "attribute",
    status: "implemented",
    // AI safe contour authors attribute definitions (CREATE_ATTDEF /
    // UPDATE_ATTDEF / RENAME_ATTDEF_TAG / REMOVE_ATTDEF / REORDER_ATTDEFS
    // dispatcher actions) — AI-callable.
    aiCallable: true,
    aliases: { en: ["ATTDEF", "ATT"], ru: ["АТРИБУТ", "АТТРИБУТ"] },
    commandTypeLiterals: ["geometry/create-attdef"],
    draftCommandId: "ATTDEF",
    draftSteps: [
      {
        stepId: "attdef-dialog",
        promptKeyRu: "prompt.attdef.dialog_open",
        promptKeyEn: "prompt.attdef.dialog_open",
        expects: "dialog",
      },
    ],
    parameters: [
      {
        name: "blockName",
        type: "string",
        required: true,
        description: {
          ru: "Имя блока, в определение которого добавляется ATTDEF (должно совпадать с активной BEDIT-сессией).",
          en: "Name of the block whose definition the ATTDEF is added to (must match the active BEDIT session).",
        },
      },
      {
        name: "attdef.tag",
        type: "string",
        required: true,
        description: {
          ru: "Тег атрибута (DXF group 2). Используется как ключ при ATTRIB-инстанциировании.",
          en: "Attribute tag (DXF group 2). Used as the key when ATTRIBs are instantiated.",
        },
      },
      {
        name: "attdef.prompt",
        type: "string",
        required: false,
        description: {
          ru: "Подсказка, выводимая пользователю при заполнении ATTRIB на INSERT.",
          en: "Prompt shown to the user when filling ATTRIB values on an INSERT.",
        },
      },
      {
        name: "attdef.defaultValue",
        type: "string",
        required: false,
        description: {
          ru: "Значение по умолчанию для новых ATTRIB-экземпляров.",
          en: "Default value for new ATTRIB instances.",
        },
      },
      {
        name: "attdef.insertionPoint",
        type: "point2d",
        required: true,
        description: {
          ru: "Точка вставки текста ATTDEF (в координатах определения блока).",
          en: "Insertion point of the ATTDEF text (in block-definition coordinates).",
        },
      },
      {
        name: "attdef.height",
        type: "number",
        required: true,
        description: {
          ru: "Высота текста ATTDEF (положительное число).",
          en: "ATTDEF text height (positive number).",
        },
      },
      {
        name: "attdef.rotation",
        type: "number",
        required: false,
        defaultValue: "0",
        description: {
          ru: "Угол поворота текста ATTDEF в радианах.",
          en: "Rotation angle of the ATTDEF text, in radians.",
        },
      },
      {
        name: "attdef.widthFactor",
        type: "number",
        required: false,
        defaultValue: "1",
        description: {
          ru: "Коэффициент ширины символов (DXF group 41).",
          en: "Character width factor (DXF group 41).",
        },
      },
      {
        name: "attdef.obliqueAngle",
        type: "number",
        required: false,
        defaultValue: "0",
        description: {
          ru: "Наклон символов в радианах (DXF group 51).",
          en: "Character oblique angle in radians (DXF group 51).",
        },
      },
      {
        name: "attdef.styleName",
        type: "string",
        required: false,
        defaultValue: "Standard",
        description: {
          ru: "Имя текстового стиля ATTDEF.",
          en: "Text style name for the ATTDEF.",
        },
      },
      {
        name: "attdef.hJustification",
        type: "number",
        required: false,
        defaultValue: "0",
        description: {
          ru: "Горизонтальная привязка (DXF group 72, 0..5).",
          en: "Horizontal justification (DXF group 72, 0..5).",
        },
      },
      {
        name: "attdef.vJustification",
        type: "number",
        required: false,
        defaultValue: "0",
        description: {
          ru: "Вертикальная привязка (DXF group 74, 0..3).",
          en: "Vertical justification (DXF group 74, 0..3).",
        },
      },
      {
        name: "attdef.flags",
        type: "object",
        required: true,
        description: {
          ru: "Флаги ATTDEF: invisible, constant, verify, preset, lockPosition, multiline (DXF group 70 биты).",
          en: "ATTDEF flags: invisible, constant, verify, preset, lockPosition, multiline (DXF group 70 bits).",
        },
      },
      {
        name: "attdef.backgroundFill",
        type: "boolean",
        required: false,
        defaultValue: "false",
        description: {
          ru: "Заливка фона текста ATTDEF.",
          en: "ATTDEF text background fill.",
        },
      },
      {
        name: "attdef.backgroundColor",
        type: "string",
        required: false,
        description: {
          ru: "Цвет фона ATTDEF (когда backgroundFill=true).",
          en: "ATTDEF background color (when backgroundFill=true).",
        },
      },
      {
        name: "attdef.fillBoxScale",
        type: "number",
        required: false,
        defaultValue: "1.5",
        description: {
          ru: "Коэффициент рамки фона относительно высоты текста.",
          en: "Background fill box scale relative to text height.",
        },
      },
      {
        name: "layer",
        type: "string",
        required: false,
        defaultValue: "0",
        description: {
          ru: "Слой ATTDEF.",
          en: "Layer the ATTDEF is authored on.",
        },
      },
      {
        name: "color",
        type: "string",
        required: false,
        defaultValue: "ByLayer",
        description: {
          ru: "Цвет ATTDEF (ByLayer / ByBlock / именованный / индекс).",
          en: "ATTDEF color (ByLayer / ByBlock / name / index).",
        },
      },
      {
        name: "linetype",
        type: "string",
        required: false,
        defaultValue: "ByLayer",
        description: { ru: "Тип линии ATTDEF.", en: "ATTDEF linetype." },
      },
      {
        name: "lineweight",
        type: "string",
        required: false,
        defaultValue: "ByLayer",
        description: { ru: "Вес линии ATTDEF.", en: "ATTDEF lineweight." },
      },
    ],
    help: {
      ru: {
        short: "Создать определение атрибута внутри редактора блоков.",
        long: "Команда ATTDEF добавляет в активное определение блока (BEDIT) новое поле-атрибут: тег, подсказка, значение по умолчанию, текстовые свойства (стиль, высота, поворот, привязки), флаги (Invisible, Constant, Verify, Preset, Lock Position, Multi-line). Дальнейшие INSERTы блока получают по такому ATTDEF соответствующий ATTRIB-экземпляр со значением, который пользователь вводит в диалоге Insert. Команда работает только внутри BEDIT-сессии — иначе reducer молча отклоняет вызов.",
        examples: ["ATTDEF", "АТРИБУТ", "ATT"],
        seeAlso: ["BEDIT", "DDATTE", "EATTEDIT", "ATTSYNC"],
      },
      en: {
        short:
          "Create an ATTDEF (block attribute definition) inside the Block Editor.",
        long: "The ATTDEF command adds a new attribute field to the active block definition (BEDIT): tag, prompt, default value, text properties (style, height, rotation, justifications), and flags (Invisible, Constant, Verify, Preset, Lock Position, Multi-line). Subsequent INSERTs of the block receive a matching ATTRIB instance whose value is entered through the Insert dialog. The command is only valid inside a BEDIT session — otherwise the reducer silently rejects it.",
        examples: ["ATTDEF", "ATT"],
        seeAlso: ["BEDIT", "DDATTE", "EATTEDIT", "ATTSYNC"],
      },
    },
    uiMetadata: {
      labelKeyRu: "command.attdef.label",
      labelKeyEn: "command.attdef.label",
      ribbonTab: "draw",
      ribbonSection: "block",
      glyph: null,
    },
    reducerLocations: [
      { commandTypeLiteral: "geometry/create-attdef", file: GEOMETRY_REDUCER },
    ],
  },

  // DDATTE / ATTEDIT — `normalizeDraftCommand` collapses ATTEDIT into
  // DDATTE; both open the same simple table editor.
  {
    id: "DDATTE",
    category: "attribute",
    status: "implemented",
    // aiCallable:false — not in ТЗ §10.1 + no shipped ai-commands action targets
    // DDATTE (the UPDATE_ATTRIBUTE action targets EATTEDIT, not DDATTE); AI surface
    // deferred to CC-2c + explicit ТЗ §17 revision.
    aiCallable: false,
    aliases: { en: ["DDATTE", "ATTEDIT"], ru: ["АТРЕД", "РЕДАТР"] },
    commandTypeLiterals: [
      "geometry/update-attribute-value",
      "geometry/update-attribute-values",
    ],
    draftCommandId: "DDATTE",
    draftSteps: [
      {
        stepId: "ddatte-select",
        promptKeyRu: "prompt.ddatte.select",
        promptKeyEn: "prompt.ddatte.select",
        expects: "selection",
      },
      {
        stepId: "ddatte-dialog",
        promptKeyRu: "prompt.ddatte.dialog_open",
        promptKeyEn: "prompt.ddatte.dialog_open",
        expects: "dialog",
      },
    ],
    parameters: [
      {
        name: "insertId",
        type: "entityId",
        required: true,
        description: {
          ru: "INSERT, чьи ATTRIB значения редактируются.",
          en: "INSERT whose ATTRIB values are being edited.",
        },
      },
      {
        name: "tag",
        type: "string",
        required: true,
        description: {
          ru: "Тег ATTRIB, которому присваивается newValue.",
          en: "Tag of the ATTRIB to be assigned newValue.",
        },
      },
      {
        name: "newValue",
        type: "string",
        required: false,
        description: {
          ru: "Новое значение ATTRIB. Пропуск (undefined) очищает значение.",
          en: "New ATTRIB value. Omitting (undefined) clears the value.",
        },
      },
      {
        name: "updates",
        type: "object",
        required: false,
        description: {
          ru: "Массив изменений {tag, newValue, overrides} — используется кнопкой Save (literal geometry/update-attribute-values).",
          en: "Array of {tag, newValue, overrides} updates — used by the Save button (literal geometry/update-attribute-values).",
        },
      },
    ],
    help: {
      ru: {
        short: "Редактирование значений атрибутов выбранного INSERT.",
        long: "Команда DDATTE / ATTEDIT открывает простой табличный редактор атрибутов выбранного блока-вставки. Пользователь видит список ATTRIB (видимых) с тегами и значениями, изменяет нужные строки и нажимает Save — изменения применяются батчем через geometry/update-attribute-values, что делает их единым undo-шагом. Constant-теги скрыты как нередактируемые (соответствие AutoCAD).",
        examples: ["DDATTE", "АТРЕД 0F2A"],
        seeAlso: ["EATTEDIT", "ATTDEF", "ATTSYNC"],
      },
      en: {
        short: "Edit attribute values of the selected INSERT.",
        long: "The DDATTE / ATTEDIT command opens the simple table-style attribute editor for the selected block instance. The user sees a list of (visible) ATTRIBs with tags and values, modifies the desired rows and presses Save — changes are applied as a batch through geometry/update-attribute-values, making them a single undo step. Constant tags are hidden as non-editable (matching AutoCAD).",
        examples: ["DDATTE", "ATTEDIT 0F2A"],
        seeAlso: ["EATTEDIT", "ATTDEF", "ATTSYNC"],
      },
    },
    uiMetadata: {
      labelKeyRu: "command.ddatte.label",
      labelKeyEn: "command.ddatte.label",
      ribbonTab: "draw",
      ribbonSection: "block",
      glyph: null,
    },
    reducerLocations: [
      {
        commandTypeLiteral: "geometry/update-attribute-value",
        file: GEOMETRY_REDUCER,
      },
      {
        commandTypeLiteral: "geometry/update-attribute-values",
        file: GEOMETRY_REDUCER,
      },
    ],
  },

  // EATTEDIT — same dispatch literals as DDATTE; the difference is the
  // dialog (tabbed: Attribute / Text Options / Properties) and the optional
  // `insertProperties` payload on update-attribute-values.
  {
    id: "EATTEDIT",
    category: "attribute",
    status: "implemented",
    // Enhanced attribute editing — backed by the AI UPDATE_ATTRIBUTE /
    // UPDATE_ATTDEF safe-contour actions. AI-callable.
    aiCallable: true,
    aliases: { en: ["EATTEDIT"], ru: ["УЛАТРЕД", "УЛРЕДАТР"] },
    commandTypeLiterals: [
      "geometry/update-attribute-value",
      "geometry/update-attribute-values",
    ],
    draftCommandId: "EATTEDIT",
    draftSteps: [
      {
        stepId: "eattedit-select",
        promptKeyRu: "prompt.eattedit.select",
        promptKeyEn: "prompt.eattedit.select",
        expects: "selection",
      },
      {
        stepId: "eattedit-dialog",
        promptKeyRu: "prompt.eattedit.dialog_open",
        promptKeyEn: "prompt.eattedit.dialog_open",
        expects: "dialog",
      },
    ],
    parameters: [
      {
        name: "insertId",
        type: "entityId",
        required: true,
        description: {
          ru: "INSERT, для которого открывается улучшенный редактор атрибутов.",
          en: "INSERT for which the enhanced attribute editor is opened.",
        },
      },
      {
        name: "updates",
        type: "object",
        required: false,
        description: {
          ru: "Массив правок ATTRIB с полем overrides (override height/rotation/position/style/invisible).",
          en: "Array of ATTRIB updates with an overrides field (override height/rotation/position/style/invisible).",
        },
      },
      {
        name: "insertProperties",
        type: "object",
        required: false,
        description: {
          ru: "Патч общих свойств INSERT (layer, color, linetype, lineweight) с вкладки Properties.",
          en: "Patch for the INSERT's common properties (layer, color, linetype, lineweight) from the Properties tab.",
        },
      },
    ],
    help: {
      ru: {
        short: "Улучшенный редактор атрибутов с вкладками.",
        long: "Команда EATTEDIT открывает расширенный диалог редактора атрибутов выбранного INSERT с тремя вкладками: Attribute (значения тегов), Text Options (override стиля, высоты, поворота, позиции для конкретного ATTRIB) и Properties (layer/color/linetype/lineweight всего INSERT). Все изменения применяются батчем как единый undo-шаг.",
        examples: ["EATTEDIT", "УЛРЕДАТР 1B33"],
        seeAlso: ["DDATTE", "ATTDEF", "ATTSYNC"],
      },
      en: {
        short: "Enhanced (tabbed) attribute editor.",
        long: "The EATTEDIT command opens the enhanced attribute editor for the selected INSERT with three tabs: Attribute (tag values), Text Options (override per-ATTRIB style, height, rotation, position) and Properties (layer/color/linetype/lineweight for the whole INSERT). All edits are applied as a single batch undo step.",
        examples: ["EATTEDIT 1B33"],
        seeAlso: ["DDATTE", "ATTDEF", "ATTSYNC"],
      },
    },
    uiMetadata: {
      labelKeyRu: "command.eattedit.label",
      labelKeyEn: "command.eattedit.label",
      ribbonTab: "draw",
      ribbonSection: "block",
      glyph: null,
    },
    reducerLocations: [
      {
        commandTypeLiteral: "geometry/update-attribute-value",
        file: GEOMETRY_REDUCER,
      },
      {
        commandTypeLiteral: "geometry/update-attribute-values",
        file: GEOMETRY_REDUCER,
      },
    ],
  },

  // ATTSYNC — also covers the BATTMAN authoring literals (`remove-attdef`,
  // `update-attdef`, `rename-attdef-tag`, `reorder-attdefs`) which today
  // are dispatched only from inside the BATTMAN dialog. Once a dedicated
  // BATTMAN descriptor lands these literals migrate; until then attaching
  // them here keeps validator coverage clean without a stub descriptor.
  {
    id: "ATTSYNC",
    category: "attribute",
    status: "implemented",
    // DEFAULT-FALSE (flagged for review): attribute-definition sync across
    // existing block instances is a maintenance op with no dedicated AI
    // safe-contour action and is absent from the ТЗ §10.1 matrix. Fail-closed
    // until an explicit AI-callable decision is made.
    aiCallable: false,
    // D5 (App.tsx router→registry unification) — "СИНХРАТР" is the RU alias
    // App.tsx's CLI dispatcher actually accepted (upperStart === "ATTSYNC" ||
    // upperStart === "СИНХРАТР"); the original "АТСИНХР"/"АТТСИНХР" pair
    // predates that wiring and is kept as a documented superset so no
    // previously-typed alias stops working.
    aliases: { en: ["ATTSYNC"], ru: ["АТСИНХР", "АТТСИНХР", "СИНХРАТР"] },
    commandTypeLiterals: [
      "geometry/attsync",
      "geometry/remove-attdef",
      "geometry/update-attdef",
      "geometry/rename-attdef-tag",
      "geometry/reorder-attdefs",
    ],
    parameters: [
      {
        name: "blockName",
        type: "string",
        required: true,
        description: {
          ru: "Имя блока, чьи INSERTы синхронизируются с ATTDEF-шаблоном.",
          en: "Name of the block whose INSERTs are synced to the ATTDEF template.",
        },
      },
      {
        name: "targetInsertIds",
        type: "entityIdList",
        required: false,
        description: {
          ru: "Подмножество INSERTов для синхронизации. По умолчанию все INSERTы блока.",
          en: "Subset of INSERTs to sync. By default every INSERT of the block is processed.",
        },
      },
    ],
    help: {
      ru: {
        short: "Синхронизировать ATTRIB на INSERTах с текущими ATTDEF блока.",
        long: "Команда ATTSYNC обновляет ATTRIB строки на каждом INSERT указанного блока, приводя их в соответствие с актуальным набором ATTDEF: пользовательские значения сохраняются для тегов, которые остались, для новых ATTDEF добавляются строки со значениями по умолчанию, осиротевшие и константные ATTRIB удаляются. Если targetInsertIds задан — синхронизация ограничивается этим подмножеством. Связанные литералы remove-attdef / update-attdef / rename-attdef-tag / reorder-attdefs — диспетчеризуются из диалога BATTMAN и тоже относятся к этой группе команд.",
        examples: ["ATTSYNC", "АТСИНХР МойБлок"],
        seeAlso: ["ATTDEF", "DDATTE", "EATTEDIT"],
      },
      en: {
        short: "Sync ATTRIB rows on INSERTs with the block's current ATTDEFs.",
        long: "The ATTSYNC command updates ATTRIB rows on every INSERT of the named block, aligning them with the current ATTDEF set: user-authored values survive for tags that still exist, new ATTDEFs gain rows seeded with default values, and orphan / constant ATTRIBs are dropped. When targetInsertIds is supplied the sync is limited to that subset. Related literals remove-attdef / update-attdef / rename-attdef-tag / reorder-attdefs are dispatched from the BATTMAN dialog and belong to the same command group.",
        examples: ["ATTSYNC", "АТСИНХР MyBlock"],
        seeAlso: ["ATTDEF", "DDATTE", "EATTEDIT"],
      },
    },
    // No dedicated ATTSYNC label key exists yet in i18n; reuse the ATTDEF
    // label so the UI surface stays bilingual until a dedicated label lands.
    uiMetadata: {
      labelKeyRu: "command.attdef.label",
      labelKeyEn: "command.attdef.label",
      ribbonTab: "draw",
      ribbonSection: "block",
      glyph: null,
    },
    reducerLocations: [
      { commandTypeLiteral: "geometry/attsync", file: GEOMETRY_REDUCER },
      { commandTypeLiteral: "geometry/remove-attdef", file: GEOMETRY_REDUCER },
      { commandTypeLiteral: "geometry/update-attdef", file: GEOMETRY_REDUCER },
      {
        commandTypeLiteral: "geometry/rename-attdef-tag",
        file: GEOMETRY_REDUCER,
      },
      {
        commandTypeLiteral: "geometry/reorder-attdefs",
        file: GEOMETRY_REDUCER,
      },
    ],
  },

  // D5 (App.tsx router→registry unification) — BATTMAN / EATTEXT were
  // dispatched via literal `upperStart === "..."` comparisons in
  // App.tsx's submitCommandText before this descriptor landed. Aliases
  // below mirror exactly what the CLI already accepted.
  {
    id: "BATTMAN",
    category: "attribute",
    status: "implemented",
    // Opens the Block Attribute Manager dialog (UI surface only, no direct
    // document mutation from the CLI verb itself) — not AI-callable.
    aiCallable: false,
    aliases: { en: ["BATTMAN"], ru: ["МЕНАТР"] },
    // No CLI-dispatched literal of its own — mutations happen through the
    // dialog's own controls, which reuse the ATTSYNC-adjacent literals
    // (remove-attdef / update-attdef / rename-attdef-tag / reorder-attdefs)
    // already claimed by the ATTSYNC descriptor above.
    commandTypeLiterals: [],
    parameters: [],
    help: {
      ru: {
        short: "Открывает диспетчер атрибутов блока (Block Attribute Manager).",
        long: "Команда МЕНАТР (BATTMAN) открывает модальный диалог со списком ATTDEF-тегов выбранного (или выбираемого из выпадающего списка) блока: удаление / переименование тега, изменение подсказки, значения по умолчанию, видимости и порядка атрибутов. Изменения применяются через literals remove-attdef / update-attdef / rename-attdef-tag / reorder-attdefs, разделяемые с командой ATTSYNC.",
        examples: ["BATTMAN", "МЕНАТР"],
        seeAlso: ["ATTSYNC", "ATTDEF", "DDATTE"],
      },
      en: {
        short: "Opens the Block Attribute Manager dialog.",
        long: "The BATTMAN command opens a modal dialog listing the ATTDEF tags of a selected (or dropdown-picked) block: delete / rename a tag, edit its prompt, default value, visibility and ordering. Edits are applied through the remove-attdef / update-attdef / rename-attdef-tag / reorder-attdefs literals shared with ATTSYNC.",
        examples: ["BATTMAN", "МЕНАТР"],
        seeAlso: ["ATTSYNC", "ATTDEF", "DDATTE"],
      },
    },
    uiMetadata: {
      labelKeyRu: "command.battman.label",
      labelKeyEn: "command.battman.label",
      ribbonTab: null,
      ribbonSection: null,
      glyph: null,
    },
    reducerLocations: [],
  },

  {
    id: "EATTEXT",
    category: "attribute",
    status: "implemented",
    // Extracts attribute data to a downloadable CSV — a client-side report,
    // not a document mutation. Not AI-callable.
    aiCallable: false,
    aliases: { en: ["EATTEXT"], ru: ["ИЗВЛАТР", "ДАННЫЕ"] },
    // Pure client-side export (CSV download) — no EditorCommand dispatch.
    commandTypeLiterals: [],
    parameters: [],
    help: {
      ru: {
        short: "Извлекает данные атрибутов блоков в CSV-файл.",
        long: "Команда ИЗВЛАТР (EATTEXT) открывает диалог извлечения атрибутов: фильтрация по блокам и тегам, опции «включать невидимые» / «включать константные», предпросмотр строк, скачивание результата в формате CSV. Чисто клиентская операция — чертёж не изменяется.",
        examples: ["EATTEXT", "ИЗВЛАТР", "ДАННЫЕ"],
        seeAlso: ["ATTDEF", "ATTSYNC", "BATTMAN"],
      },
      en: {
        short: "Extracts block attribute data to a CSV file.",
        long: "The EATTEXT command opens the attribute-extraction dialog: filter by block and tag, \"include invisible\" / \"include constant\" toggles, a row preview, and a CSV download of the result. Purely client-side — the drawing is not modified.",
        examples: ["EATTEXT", "ИЗВЛАТР"],
        seeAlso: ["ATTDEF", "ATTSYNC", "BATTMAN"],
      },
    },
    uiMetadata: {
      labelKeyRu: "command.eattext.label",
      labelKeyEn: "command.eattext.label",
      ribbonTab: null,
      ribbonSection: null,
      glyph: null,
    },
    reducerLocations: [],
  },
];
